import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";
import showToast from "../components/Toast";
import { authHeader } from "../session";

// Each endpoint is a plain async function plus a `useX` react-query hook, the
// same convention the public client follows. Components import the hooks.

const errorMessage = (error, fallback) =>
  error.response?.data?.error || fallback;

// --------------------------------- Read --------------------------------------

export const getPlayers = async (params) => {
  const response = await axios.get("/api/v1/admin/players", {
    params,
    headers: authHeader(),
  });
  return response.data; // { data, totalResults, currentPage, totalPages }
};

export const usePlayers = (params) =>
  useQuery(["players", params], () => getPlayers(params), {
    keepPreviousData: true, // stops the table blanking between pages
    refetchOnWindowFocus: false,
  });

// -------------------------------- Write --------------------------------------

export const createPlayer = async (player) => {
  const response = await axios.post("/api/v1/admin/players", player, {
    headers: authHeader(),
  });
  return response.data.data;
};

export const useCreatePlayer = () => {
  const queryClient = useQueryClient();

  return useMutation(createPlayer, {
    onSuccess: (player) => {
      queryClient.invalidateQueries("players");
      showToast(`Added ${player.name} to the register.`);
    },
    onError: (error) =>
      showToast(errorMessage(error, "Couldn't add that player."), "error"),
  });
};

export const updatePlayer = async ({ id, ...player }) => {
  const response = await axios.put(`/api/v1/admin/players/${id}`, player, {
    headers: authHeader(),
  });
  return response.data.data;
};

export const useUpdatePlayer = () => {
  const queryClient = useQueryClient();

  return useMutation(updatePlayer, {
    onSuccess: (player) => {
      queryClient.invalidateQueries("players");
      showToast(`Saved changes to ${player.name}.`);
    },
    onError: (error) =>
      showToast(errorMessage(error, "Couldn't save those changes."), "error"),
  });
};

export const deletePlayer = async (id) => {
  const response = await axios.delete(`/api/v1/admin/players/${id}`, {
    headers: authHeader(),
  });
  return response.data;
};

export const useDeletePlayer = () => {
  const queryClient = useQueryClient();

  return useMutation(deletePlayer, {
    onSuccess: () => {
      queryClient.invalidateQueries("players");
      showToast("Player removed from the register.");
    },
    onError: (error) =>
      showToast(errorMessage(error, "Couldn't remove that player."), "error"),
  });
};

export const uploadPlayerPhoto = async ({ id, file }) => {
  const form = new FormData();
  form.append("image", file);

  const response = await axios.put(`/api/v1/admin/players/${id}/image`, form, {
    headers: authHeader(),
  });
  return response.data.data;
};

export const useUploadPlayerPhoto = () => {
  const queryClient = useQueryClient();

  return useMutation(uploadPlayerPhoto, {
    onSuccess: () => {
      queryClient.invalidateQueries("players");
      showToast("Photo updated.");
    },
    onError: (error) =>
      showToast(errorMessage(error, "Couldn't upload that photo."), "error"),
  });
};
